import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const operating = [
  { name: "Sellrly", tag: "Operating", note: "Performance media", url: "https://www.sellrly.com/" },
  { name: "GetLive", tag: "Operating", note: "Live and Affiliate Infrastructure", url: "https://getlive.ai/" },
  { name: "Orion", tag: "Operating", note: "Revenue + distribution platform", url: null },
  { name: "Wing", tag: "Operating", note: "Strategic venture", url: "https://www.wing.vc/" },
  { name: "Homefi", tag: "Operating", note: "Operating venture", url: "https://homefi.info/" },
];

const portfolio = [
  { name: "Tre Sensa", status: "Acquired 2021", url: "https://liftoff.ai/" },
  { name: "Franklin Group", status: "Acquired 2018", url: "https://franklingroupusa.com/" },
  { name: "Tapad", status: "Acquired 2016", url: "https://www.tapad.com/" },
  { name: "Elite Daily", status: "Acquired 2015", url: "https://www.elitedaily.com/" },
  { name: "Reelcontent", status: "Acquired", url: null },
  { name: "Fullscreen × Reolio", status: "Acquired 2018", url: null },
  { name: "Genesis Media", status: "Acquired 2017", url: "https://www.genesismedia.co/" },
  { name: "PubGears", status: "Acquired 2016", url: null },
  { name: "Tremor Video", status: "IPO 2013", url: "https://www.tremor.com/" },
  { name: "Cquell", status: "Acquired", url: null },
  { name: "Bombfell", status: "Acquired", url: null },
];

function Card({ name, status, testid, large = false, url = null }) {
  const inner = (
    <>
      <span className="absolute top-4 right-4 font-mono-label text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
        {status}
      </span>
      <span className="absolute top-4 left-4 font-mono-label text-[10px] tracking-[0.2em] uppercase text-muted-foreground transition-colors group-hover:text-foreground">
        ◆
      </span>
      <span
        className={`font-display font-medium text-center tracking-tight leading-[0.95] ${
          large ? "text-3xl md:text-5xl" : "text-2xl md:text-4xl"
        }`}
      >
        {name}
      </span>
      {url && (
        <span className="absolute bottom-4 right-4 font-mono-label text-[10px] tracking-[0.2em] uppercase text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
          Visit ↗
        </span>
      )}
    </>
  );

  const baseClasses =
    "group relative aspect-[4/3] border-r border-b border-border p-6 md:p-8 flex items-center justify-center transition-colors";

  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer noopener"
        data-testid={testid}
        className={`${baseClasses} hover:bg-foreground hover:text-background cursor-pointer focus:outline-none focus:bg-foreground focus:text-background`}
      >
        {inner}
      </a>
    );
  }

  return (
    <div
      data-testid={testid}
      className={`${baseClasses} hover:bg-secondary/60`}
    >
      {inner}
    </div>
  );
}

export default function Portfolio() {
  return (
    <div data-testid="page-portfolio">
      {/* HEADER */}
      <section className="container-x pt-12 md:pt-20 pb-16">
        <p className="eyebrow mb-8">Portfolio</p>
        <h1
          className="hero-headline text-[40px] sm:text-5xl md:text-7xl lg:text-[88px] max-w-[16ch]"
          data-testid="portfolio-headline"
        >
          Companies we’ve built, backed and operated.
        </h1>
        <p className="mt-10 max-w-2xl text-base md:text-lg font-light leading-relaxed text-foreground/80">
          Selected investments, operating companies and prior involvements — spanning advertising technology, commerce infrastructure, live shopping, marketplaces and consumer media.
        </p>
      </section>

      {/* OPERATING */}
      <section className="border-t border-border">
        <div className="container-x py-16 md:py-24">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <p className="eyebrow mb-3">Operating Companies</p>
              <h2 className="font-display text-2xl md:text-4xl tracking-tight">
                Current ventures & active operations.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-border">
            {operating.map((o) => (
              <Card
                key={o.name}
                name={o.name}
                status={o.note}
                testid={`portfolio-operating-${o.name.toLowerCase()}`}
                url={o.url}
                large
              />
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO GRID */}
      <section className="border-t border-border bg-secondary/30">
        <div className="container-x py-16 md:py-24">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <p className="eyebrow mb-3">Track Record</p>
              <h2 className="font-display text-2xl md:text-4xl tracking-tight">
                Prior investments &amp; exits.
              </h2>
            </div>
            <p className="font-mono-label text-[11px] tracking-[0.22em] uppercase text-muted-foreground">
              {portfolio.length} Companies
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 border-t border-l border-border bg-background">
            {portfolio.map((c) => (
              <Card
                key={c.name}
                name={c.name}
                status={c.status}
                url={c.url}
                testid={`portfolio-card-${c.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border">
        <div className="container-x py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <h2 className="md:col-span-8 font-display text-3xl md:text-5xl tracking-tight leading-[1.05]">
            Have something we should be building, backing, or scaling?
          </h2>
          <div className="md:col-span-4 md:justify-self-end">
            <Link
              to="/contact"
              className="btn-primary"
              data-testid="portfolio-cta-contact"
            >
              Get in Touch <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
